import React, {
	createContext,
	useState,
	useContext,
	Dispatch,
	useEffect,
} from "react";

type DataType = Record<string, any>;

export type SearchCriterion = {
	searchColumn: any;
	submittedSearchText: string | string[];
};

interface UseDataTableContextProps {
	isModalOpen: boolean;
	setIsModalOpen: Dispatch<React.SetStateAction<boolean>>;
	toggleModal: () => void;
	closeModal: () => void;
	activeIndex: number | null;
	setActiveIndex: (index: number | null) => void;
	handleRowClick: (
		index: number,
		rowUuid: string,
		event: React.MouseEvent<HTMLTableRowElement>
	) => void;
	activeSortColumn: string | null;
	setActiveSortColumn: (columnId: string | null) => void;
	activeUuid: string | null;
	setActiveUuid: (uuid: string | null) => void;
	searchDropdownValue: string | null;
	setSearchDropdownValue: Dispatch<React.SetStateAction<string | null>>;
	searchColumn: string;
	setSearchColumn: Dispatch<React.SetStateAction<string>>;
	switchContent: boolean;
	setSwitchContent: Dispatch<React.SetStateAction<boolean>>;
	data: DataType[];
	activeRowUuid: string | null;
	setActiveRowUuid: Dispatch<React.SetStateAction<string | null>>;
	deactivatedQuickMenuIds: string[];
	setDeactivatedQuickMenuIds: Dispatch<React.SetStateAction<string[]>>;
	searchCriteria: SearchCriterion[];
	setSearchCriteria: Dispatch<React.SetStateAction<SearchCriterion[]>>;
	setHasSort: Dispatch<React.SetStateAction<boolean>>;
	hasSort: boolean;
	toggleStates: Record<string, boolean>;
	setToggleStates: Dispatch<React.SetStateAction<Record<string, boolean>>>;
	modalTableOpened: boolean;
	setModalTableOpened: Dispatch<React.SetStateAction<boolean>>;
	displayStickyColumn?: boolean;
	setDisplayStickyColumn?: Dispatch<React.SetStateAction<boolean>>;
	hasInfiniteScroll?: boolean;
	infiniteScrollIsLoadingMore?: boolean;
	setInfiniteScrollIsLoadingMore?: Dispatch<React.SetStateAction<boolean>>;
	totalRecordsFromTable?: number;
	setTotalRecordsFromTable?: Dispatch<React.SetStateAction<number>>;
	expandedCells: Record<string, boolean>;
	toggleCell: (key: string) => void;
	isNestedTable: boolean;
	setIsNestedTable: Dispatch<React.SetStateAction<boolean>>;
	toggleRow: (uuid: string) => void;
	isNestedChildTable: boolean;
	isNestedChildTableModalToggle: boolean;
	setIsNestedChildTableModalToggle: Dispatch<React.SetStateAction<boolean>>;
	leftLockedColumns: number;
	setLeftLockedColumns: Dispatch<React.SetStateAction<number>>;
	columnHeaders: string[];
	setColumnHeaders: Dispatch<React.SetStateAction<string[]>>;
	hasCustomCellToggle: boolean;
	setHasCustomCellToggle: Dispatch<React.SetStateAction<boolean>>;
	isSubTable?: boolean;
	rowsHaveModalOnClick: boolean;
}

const initialContextData: UseDataTableContextProps = {
	isModalOpen: false,
	setIsModalOpen: () => {},
	toggleModal: () => {},
	closeModal: () => {},
	activeIndex: null,
	setActiveIndex: () => {},
	handleRowClick: () => {},
	activeSortColumn: null,
	setActiveSortColumn: () => {},
	activeUuid: null,
	setActiveUuid: () => {},
	searchDropdownValue: null,
	setSearchDropdownValue: () => {},
	searchColumn: "",
	setSearchColumn: () => {},
	switchContent: false,
	setSwitchContent: () => {},
	data: [],
	activeRowUuid: "",
	setActiveRowUuid: () => {},
	deactivatedQuickMenuIds: [],
	setDeactivatedQuickMenuIds: () => {},
	searchCriteria: [],
	setSearchCriteria: () => {},
	hasSort: false,
	setHasSort: () => {},
	toggleStates: {},
	setToggleStates: () => {},
	modalTableOpened: false,
	setModalTableOpened: () => {},
	displayStickyColumn: true,
	setDisplayStickyColumn: () => {},
	hasInfiniteScroll: false,
	infiniteScrollIsLoadingMore: false,
	setInfiniteScrollIsLoadingMore: () => {},
	totalRecordsFromTable: 0,
	setTotalRecordsFromTable: () => {},
	expandedCells: {},
	toggleCell: () => {},
	isNestedTable: false,
	setIsNestedTable: () => {},
	toggleRow: () => {},
	isNestedChildTable: false,
	isNestedChildTableModalToggle: false,
	setIsNestedChildTableModalToggle: () => {},
	leftLockedColumns: 0,
	setLeftLockedColumns: () => {},
	columnHeaders: [],
	setColumnHeaders: () => {},
	hasCustomCellToggle: false,
	setHasCustomCellToggle: () => {},
	isSubTable: false,
	rowsHaveModalOnClick: true,
};

const DataTableContext =
	createContext<UseDataTableContextProps>(initialContextData);

export const useDataTableContext = () => useContext(DataTableContext);

export const DataTableContextProvider: React.FC<{
	children: React.ReactNode;
	hasModalTable: boolean;
	data: DataType[];
	showStickyColumn: boolean;
	hasInfiniteScroll?: boolean;
	isNestedChildTable?: boolean;
	isNestedTable?: boolean;
	isSubTable?: boolean;
	rowsHaveModalOnClick?: boolean;
}> = ({
	children,
	hasModalTable,
	data,
	hasInfiniteScroll = false,
	showStickyColumn,
	isNestedChildTable = false,
	isSubTable = false,
	rowsHaveModalOnClick = true,
}) => {
	const [hasCustomCellToggle, setHasCustomCellToggle] =
		useState<boolean>(false);
	const [columnHeaders, setColumnHeaders] = useState<string[]>([]);
	const [searchCriteria, setSearchCriteria] = useState<SearchCriterion[]>([]);
	const [hasSort, setHasSort] = useState<boolean>(false);
	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
	const [activeIndex, setActiveIndex] = useState<number | null>(null);
	const [activeSortColumn, setActiveSortColumn] = useState<string | null>(
		null
	);
	const [activeUuid, setActiveUuid] = useState<string | null>(null);
	const [searchDropdownValue, setSearchDropdownValue] = useState<
		string | null
	>(null);
	const [searchColumn, setSearchColumn] = useState<string>("");
	const [switchContent, setSwitchContent] = useState<boolean>(false);
	const [activeRowUuid, setActiveRowUuid] = useState<string | null>(null);
	const toggleRow = React.useCallback((uuid: string) => {
		setActiveRowUuid((prev) => (prev === uuid ? null : uuid));
	}, []);
	const [deactivatedQuickMenuIds, setDeactivatedQuickMenuIds] = useState<
		string[]
	>([]);
	const [toggleStates, setToggleStates] = useState<Record<string, boolean>>(
		{}
	);
	const [modalTableOpened, setModalTableOpened] =
		useState<boolean>(hasModalTable);
	const [displayStickyColumn, setDisplayStickyColumn] =
		useState<boolean>(showStickyColumn);

	const [isNestedTable, setIsNestedTable] = useState<boolean>(false);
	const [leftLockedColumns, setLeftLockedColumns] = useState<number>(0);

	// Restore from localStorage if desired
	const [infiniteScrollIsLoadingMore, setInfiniteScrollIsLoadingMore] =
		useState<boolean>(false);
	const [totalRecordsFromTable, setTotalRecordsFromTable] =
		useState<number>(0);
	const [expandedCells, setExpandedCells] = useState<Record<string, boolean>>(
		{}
	);

	const toggleCell = React.useCallback((key: string) => {
		setExpandedCells((s) => ({ ...s, [key]: !s[key] }));
	}, []);

	const toggleModal = () => {
		setIsModalOpen((prev) => !prev);
	};

	const closeModal = () => {
		setIsModalOpen(false);
	};

	const [isNestedChildTableModalToggle, setIsNestedChildTableModalToggle] =
		useState<boolean>(false);

	const handleRowClick = (
		index: number,
		rowUuid: string,
		event: React.MouseEvent<HTMLTableRowElement>
	) => {
		setActiveIndex(index);

		if (rowsHaveModalOnClick) {
			setIsModalOpen(true);
			setActiveUuid(rowUuid);
			setSwitchContent(false);
		} else if (hasModalTable) {
			setIsModalOpen(true);
			setActiveUuid(rowUuid);
			setSwitchContent(true);
		} else {
			setActiveUuid(rowUuid);
			setSwitchContent(true);
		}
		if (data && data[index] && data[index].data && data[index].data.uuid) {
			setActiveUuid(data[index].data.uuid);
		} else {
			setActiveUuid(rowUuid);
		}
	};

	useEffect(() => {
		// don’t auto-open for nested child tables
		if (isNestedChildTable) return;

		const params = new URLSearchParams(location.search);
		const uuidInUrl = params.get("uuid");
		if (uuidInUrl) {
			setIsModalOpen(true);
			setActiveUuid(uuidInUrl);
		}
	}, [location.search, isNestedChildTable]);

	// Include 'hasInfiniteScroll' in the context
	const contextValue: UseDataTableContextProps = {
		isModalOpen,
		setIsModalOpen,
		toggleModal,
		closeModal,
		activeIndex,
		setActiveIndex,
		handleRowClick,
		activeSortColumn,
		setActiveSortColumn,
		activeUuid,
		setActiveUuid,
		searchDropdownValue,
		setSearchDropdownValue,
		searchColumn,
		setSearchColumn,
		switchContent,
		setSwitchContent,
		data,
		activeRowUuid,
		setActiveRowUuid,
		deactivatedQuickMenuIds,
		setDeactivatedQuickMenuIds,
		searchCriteria,
		setSearchCriteria,
		hasSort,
		setHasSort,
		toggleStates,
		setToggleStates,
		modalTableOpened,
		setModalTableOpened,
		displayStickyColumn,
		setDisplayStickyColumn,
		hasInfiniteScroll,
		infiniteScrollIsLoadingMore,
		setInfiniteScrollIsLoadingMore,
		totalRecordsFromTable,
		setTotalRecordsFromTable,
		expandedCells,
		toggleCell,
		isNestedTable,
		setIsNestedTable,
		toggleRow,
		isNestedChildTable,
		isNestedChildTableModalToggle,
		setIsNestedChildTableModalToggle,
		leftLockedColumns,
		setLeftLockedColumns,
		columnHeaders,
		setColumnHeaders,
		hasCustomCellToggle,
		setHasCustomCellToggle,
		isSubTable,
		rowsHaveModalOnClick,
	};

	return (
		<DataTableContext.Provider value={contextValue}>
			{children}
		</DataTableContext.Provider>
	);
};

export default useDataTableContext;
